import React, { useEffect, useRef, useState } from "react";
import { Stage, Layer, Image as KonvaImage, Rect, Transformer, Text, Line } from "react-konva";
import type Konva from "konva";
import type { BuilderItem } from "../../types/gangsheet";
import {
  SHEET_WIDTH_IN,
  MIN_ITEM_SIZE_IN,
  clampCenterToSheet,
  getDpi,
  getDpiTone,
  getRotatedCorners,
  inchesToPreviewPx,
  normalizeAngle,
  previewPxToInches,
} from "../../utils/gangsheetMath";

type Props = {
  items: BuilderItem[];
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  updateItem: (id: string, updater: (item: BuilderItem) => BuilderItem) => void;
  overlapMap: Set<string>;
  outOfBoundsMap: Set<string>;
  sheetLengthIn: number;
  stageWidth: number;
  stageHeight: number;
  stageScale: number;
  stageRef: React.MutableRefObject<Konva.Stage | null>;
  trRef: React.MutableRefObject<Konva.Transformer | null>;
  shapeRefs: React.MutableRefObject<Record<string, Konva.Image | null>>;
};

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

function clampZoom(value: number) {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, value));
}

export default function GangsheetCanvas({
  items,
  selectedId,
  setSelectedId,
  updateItem,
  overlapMap,
  outOfBoundsMap,
  sheetLengthIn,
  stageWidth,
  stageHeight,
  stageScale,
  stageRef,
  trRef,
  shapeRefs,
}: Props) {
  const [viewportScale, setViewportScale] = useState(1);
  const [viewportPos, setViewportPos] = useState({ x: 0, y: 0 });

  const pinchActiveRef = useRef(false);
  const lastPinchDistanceRef = useRef(0);
  const lastPanCenterRef = useRef<{ x: number; y: number } | null>(null);

  const rightMousePanningRef = useRef(false);
  const rightMouseMovedRef = useRef(false);
  const lastMousePanPointRef = useRef<{ x: number; y: number } | null>(null);

  const updateCursor = () => {
    const container = stageRef.current?.container();
    if (!container) return;

    if (rightMousePanningRef.current) {
      container.style.cursor = "grabbing";
      return;
    }

    container.style.cursor = "default";
  };

  useEffect(() => {
    updateCursor();
  }, []);

  const getTouchCenter = (touch1: Touch, touch2: Touch) => ({
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2,
  });

  const getTouchDistance = (touch1: Touch, touch2: Touch) =>
    Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);

  const zoomAtPoint = (pointerX: number, pointerY: number, nextScaleRaw: number) => {
    setViewportScale((currentScale) => {
      const nextScale = clampZoom(nextScaleRaw);

      setViewportPos((currentPos) => {
        const pointTo = {
          x: (pointerX - currentPos.x) / currentScale,
          y: (pointerY - currentPos.y) / currentScale,
        };

        return {
          x: pointerX - pointTo.x * nextScale,
          y: pointerY - pointTo.y * nextScale,
        };
      });

      return nextScale;
    });
  };

  const handleWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();

    const stage = stageRef.current;
    if (!stage) return;

    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const scaleBy = 1.06;
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const nextScale =
      direction > 0 ? viewportScale * scaleBy : viewportScale / scaleBy;

    zoomAtPoint(pointer.x, pointer.y, nextScale);
  };

  const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const isRightButton = e.evt.button === 2;

    if (isRightButton) {
      e.evt.preventDefault();
      rightMousePanningRef.current = true;
      rightMouseMovedRef.current = false;
      lastMousePanPointRef.current = {
        x: e.evt.clientX,
        y: e.evt.clientY,
      };
      updateCursor();
      return;
    }

    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      setSelectedId(null);
    }
  };

  const handleMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!rightMousePanningRef.current || !lastMousePanPointRef.current) return;

    e.evt.preventDefault();

    const currentPoint = {
      x: e.evt.clientX,
      y: e.evt.clientY,
    };

    const deltaX = currentPoint.x - lastMousePanPointRef.current.x;
    const deltaY = currentPoint.y - lastMousePanPointRef.current.y;

    if (deltaX !== 0 || deltaY !== 0) {
      rightMouseMovedRef.current = true;
    }

    setViewportPos((prev) => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));

    lastMousePanPointRef.current = currentPoint;
  };

  const endRightMousePan = () => {
    if (!rightMousePanningRef.current) return;

    rightMousePanningRef.current = false;
    lastMousePanPointRef.current = null;

    window.setTimeout(() => {
      rightMouseMovedRef.current = false;
      updateCursor();
    }, 0);
  };

  const handleTouchStart = (e: Konva.KonvaEventObject<TouchEvent>) => {
    const touches = e.evt.touches;

    if (touches.length === 2) {
      pinchActiveRef.current = true;
      lastPinchDistanceRef.current = getTouchDistance(touches[0], touches[1]);
      lastPanCenterRef.current = getTouchCenter(touches[0], touches[1]);
      return;
    }

    if (touches.length === 1) {
      const clickedOnEmpty = e.target === e.target.getStage();
      if (clickedOnEmpty) {
        setSelectedId(null);
      }
    }
  };

  const handleTouchMove = (e: Konva.KonvaEventObject<TouchEvent>) => {
    const touches = e.evt.touches;

    if (touches.length !== 2) return;

    e.evt.preventDefault();
    pinchActiveRef.current = true;

    const touch1 = touches[0];
    const touch2 = touches[1];

    const currentDistance = getTouchDistance(touch1, touch2);
    const currentCenter = getTouchCenter(touch1, touch2);

    if (!lastPinchDistanceRef.current || !lastPanCenterRef.current) {
      lastPinchDistanceRef.current = currentDistance;
      lastPanCenterRef.current = currentCenter;
      return;
    }

    const distanceRatio = currentDistance / lastPinchDistanceRef.current;

    setViewportScale((currentScale) => {
      const nextScale = clampZoom(currentScale * distanceRatio);

      setViewportPos((currentPos) => {
        const pointTo = {
          x: (currentCenter.x - currentPos.x) / currentScale,
          y: (currentCenter.y - currentPos.y) / currentScale,
        };

        const zoomedPos = {
          x: currentCenter.x - pointTo.x * nextScale,
          y: currentCenter.y - pointTo.y * nextScale,
        };

        const panDelta = {
          x: currentCenter.x - lastPanCenterRef.current!.x,
          y: currentCenter.y - lastPanCenterRef.current!.y,
        };

        return {
          x: zoomedPos.x + panDelta.x,
          y: zoomedPos.y + panDelta.y,
        };
      });

      return nextScale;
    });

    lastPinchDistanceRef.current = currentDistance;
    lastPanCenterRef.current = currentCenter;
  };

  const handleTouchEnd = (e: Konva.KonvaEventObject<TouchEvent>) => {
    const remainingTouches = e.evt.touches.length;

    if (remainingTouches < 2) {
      lastPinchDistanceRef.current = 0;
      lastPanCenterRef.current = null;

      window.setTimeout(() => {
        pinchActiveRef.current = false;
      }, 0);
    }
  };

  return (
    <Stage
      width={stageWidth}
      height={stageHeight}
      ref={(node) => {
        if (node) {
          stageRef.current = node;
          node.container().oncontextmenu = (evt) => {
            evt.preventDefault();
          };
        }
      }}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={endRightMousePan}
      onMouseLeave={endRightMousePan}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <Layer
        scaleX={viewportScale}
        scaleY={viewportScale}
        x={viewportPos.x}
        y={viewportPos.y}
      >
        <Rect
          x={0}
          y={0}
          width={stageWidth}
          height={stageHeight}
          fill="#ffffff"
          stroke="rgba(59,130,246,0.35)"
          strokeWidth={1}
        />

        {Array.from({ length: Math.floor(sheetLengthIn) + 1 }).map((_, idx) => {
          const y = inchesToPreviewPx(idx) * stageScale;

          return (
            <React.Fragment key={idx}>
              <Rect
                x={0}
                y={y}
                width={stageWidth}
                height={1}
                fill={
                  idx % 6 === 0
                    ? "rgba(59,130,246,0.25)"
                    : "rgba(15,23,42,0.08)"
                }
              />
              {idx < sheetLengthIn && (
                <Text
                  x={6}
                  y={y + 4}
                  text={`${idx}"`}
                  fontSize={11}
                  fill="rgba(30,64,175,0.7)"
                  listening={false}
                />
              )}
            </React.Fragment>
          );
        })}

        {Array.from({ length: SHEET_WIDTH_IN + 1 }).map((_, idx) => {
          const x = inchesToPreviewPx(idx) * stageScale;

          return (
            <Rect
              key={`v-${idx}`}
              x={x}
              y={0}
              width={1}
              height={stageHeight}
              fill={
                idx % 2 === 0
                  ? "rgba(15,23,42,0.08)"
                  : "rgba(59,130,246,0.08)"
              }
            />
          );
        })}

        {items.map((item) => {
          const x = inchesToPreviewPx(item.centerX) * stageScale;
          const y = inchesToPreviewPx(item.centerY) * stageScale;
          const imageWidthPx = inchesToPreviewPx(item.widthIn) * stageScale;
          const imageHeightPx = inchesToPreviewPx(item.heightIn) * stageScale;

          const overlapping = overlapMap.has(item.id);
          const outOfBounds = outOfBoundsMap.has(item.id);
          const dpi = getDpi(item);
          const tone = getDpiTone(dpi);
          const isSelectedItem = selectedId === item.id;

          const corners = getRotatedCorners(item).map((point) => [
            inchesToPreviewPx(point.x) * stageScale,
            inchesToPreviewPx(point.y) * stageScale,
          ]);

          const flatPoints = corners.flat();

          return (
            <React.Fragment key={item.id}>
              <KonvaImage
                ref={(node) => {
                  shapeRefs.current[item.id] = node;
                }}
                x={x}
                y={y}
                offsetX={imageWidthPx / 2}
                offsetY={imageHeightPx / 2}
                rotation={item.rotation}
                image={item.imageEl}
                width={imageWidthPx}
                height={imageHeightPx}
                draggable={!pinchActiveRef.current && !rightMousePanningRef.current}
                onClick={() => {
                  if (!rightMouseMovedRef.current) {
                    setSelectedId(item.id);
                  }
                }}
                onTap={() => {
                  if (!pinchActiveRef.current) {
                    setSelectedId(item.id);
                  }
                }}
                onDragStart={() => {
                  setSelectedId(item.id);
                }}
                onDragEnd={(e) => {
                  const node = e.target as Konva.Image;

                  const localX = node.x();
                  const localY = node.y();

                  const nextCenterX = previewPxToInches(localX / stageScale);
                  const nextCenterY = previewPxToInches(localY / stageScale);

                  updateItem(item.id, (prev) =>
                    clampCenterToSheet(
                      {
                        ...prev,
                        centerX: nextCenterX,
                        centerY: nextCenterY,
                      },
                      sheetLengthIn
                    )
                  );
                }}
                onTransformEnd={(e) => {
                  const node = e.target as Konva.Image;
                  const scaleX = node.scaleX();
                  const scaleY = node.scaleY();

                  const nextWidthPx = Math.max(20, node.width() * scaleX);
                  const nextHeightPx = Math.max(20, node.height() * scaleY);

                  const nextWidthIn = Math.max(
                    MIN_ITEM_SIZE_IN,
                    previewPxToInches(nextWidthPx / stageScale)
                  );
                  const nextHeightIn = Math.max(
                    MIN_ITEM_SIZE_IN,
                    previewPxToInches(nextHeightPx / stageScale)
                  );

                  const localX = node.x();
                  const localY = node.y();
                  const nextCenterX = previewPxToInches(localX / stageScale);
                  const nextCenterY = previewPxToInches(localY / stageScale);
                  const nextRotation = normalizeAngle(node.rotation());

                  node.scaleX(1);
                  node.scaleY(1);

                  updateItem(item.id, (prev) =>
                    clampCenterToSheet(
                      {
                        ...prev,
                        centerX: nextCenterX,
                        centerY: nextCenterY,
                        widthIn: nextWidthIn,
                        heightIn: nextHeightIn,
                        rotation: nextRotation,
                      },
                      sheetLengthIn
                    )
                  );
                }}
              />

              <Line
                points={flatPoints}
                closed
                stroke={
                  overlapping || outOfBounds
                    ? "#ef4444"
                    : isSelectedItem
                    ? "#3b82f6"
                    : tone.color
                }
                strokeWidth={isSelectedItem ? 3 : 2}
                dash={overlapping || outOfBounds ? [6, 4] : undefined}
                listening={false}
              />

              <Text
                x={Math.min(...corners.map((point) => point[0])) + 6}
                y={Math.min(...corners.map((point) => point[1])) + 6}
                text={`${dpi} DPI`}
                fontSize={13}
                fontStyle="bold"
                fill={overlapping || outOfBounds ? "#ef4444" : tone.color}
                listening={false}
              />
            </React.Fragment>
          );
        })}

        <Transformer
          ref={(node) => {
            if (node) trRef.current = node;
          }}
          rotateEnabled
          flipEnabled={false}
          keepRatio={false}
          enabledAnchors={["top-left", "top-right", "bottom-left", "bottom-right"]}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 20 || newBox.height < 20) {
              return oldBox;
            }
            return newBox;
          }}
        />
      </Layer>
    </Stage>
  );
}